import React from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const SECTIONS = [
  { id: "overview", title: "Overview" },
  { id: "late-delivery", title: "Refund for Late Delivery" },
  { id: "cancellation", title: "Cancellation Before Preparation" },
  { id: "delivered", title: "Delivered Documents" },
  { id: "revisions", title: "Free Revisions" },
  { id: "visa-rejection", title: "Visa Rejection" },
  { id: "processing", title: "Refund Processing Time" },
  { id: "how", title: "How to Request a Refund" },
  { id: "contact", title: "Contact" },
];

export default function Refunds() {
  return (
    <LegalLayout
      testId="refunds-page"
      title="Refund Policy"
      lastUpdated="February 3, 2026"
      sections={SECTIONS}
      seoTitle="Refund Policy | SchengenFile"
      seoDescription="Full refund if documents are not delivered on time or your order is cancelled before preparation. Clear rules for revisions and visa outcomes."
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          At SchengenFile we want you to feel confident when ordering your visa document package.
          This Refund Policy sets out the specific circumstances in which we will issue a full or
          partial refund, and how to request one. This Policy forms part of our{" "}
          <a href="/terms" className="text-[#2563eb] hover:underline">Terms of Service</a> and applies
          to every order placed on schengenfile.com.
        </p>
      </LegalSection>

      <LegalSection id="late-delivery" title="2. Full Refund for Late Delivery">
        <p>
          If we do not deliver your completed documents within the promised delivery window, you are
          entitled to a full refund of the amount paid for that order:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Standard delivery:</strong> within 24 hours of successful payment and receipt of all required applicant information.</li>
          <li><strong>Express delivery:</strong> within 3 hours of successful payment and receipt of all required applicant information.</li>
        </ul>
        <p>
          The delivery clock starts only after (a) your payment is confirmed by our payment provider
          and (b) you have submitted all required applicant, travel, and add-on details. Time
          waiting for information from you is not counted toward the delivery window.
        </p>
      </LegalSection>

      <LegalSection id="cancellation" title="3. Full Refund for Cancellation Before Preparation">
        <p>
          You may cancel your order and receive a full refund at any time <strong>before</strong> our
          team has begun preparing your documents. Once our team has actively started work on your
          file (which normally happens within minutes of receiving all required information), the
          order can no longer be cancelled for a full refund because time, third-party booking fees,
          and staff resources have already been committed.
        </p>
      </LegalSection>

      <LegalSection id="delivered" title="4. Delivered Documents Are Non-Refundable">
        <p>
          Once documents have been delivered to your dashboard or emailed to you, they are
          considered supplied and are <strong>not refundable</strong>. This is because our documents
          are:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Digital goods that cannot be "returned" once received;</li>
          <li>Personalised to your identity, passport, and travel plan;</li>
          <li>Prepared using paid third-party systems (flight PNRs, hotel bookings, insurance certificates) whose costs are incurred at the moment of issuance.</li>
        </ul>
        <p>
          Downloading, viewing, or forwarding any delivered document constitutes acceptance of
          delivery.
        </p>
      </LegalSection>

      <LegalSection id="revisions" title="5. Free Revisions for Our Errors">
        <p>
          If a delivered document contains an error caused by us — for example a typo we introduced
          when transcribing your details, a mistake in a date we generated, or a formatting problem —
          we will correct it free of charge, as many times as needed to make it right. Simply reply
          in your dashboard messages or email us within 14 days of delivery and we will fix it.
        </p>
        <p>
          Please note that revisions to reflect changes <em>you</em> request after delivery (for
          example, changing travel dates, hotel city, or applicant details after the file has been
          issued) may require re-issuance of the underlying booking and may incur an additional
          charge equal to the third-party cost involved. Any such fee will be communicated to you
          for approval before we proceed.
        </p>
      </LegalSection>

      <LegalSection id="visa-rejection" title="6. Visa Rejection Is Not Eligible for a Refund">
        <p>
          Visa decisions — including approvals, refusals, and annulments — are made <strong>solely
          and exclusively by the consular authorities</strong> of the destination country, based on
          criteria that are outside our control. Consulates consider factors such as your travel
          history, ties to your home country, financial situation, purpose of travel, and other
          elements of your personal profile.
        </p>
        <p>
          Because a visa rejection is not caused by our documents or our Service, refunds are{" "}
          <strong>not available</strong> in the event that your visa application is refused. This
          limitation is expressly disclosed in our Terms of Service and is a fundamental part of the
          nature of a document preparation service.
        </p>
      </LegalSection>

      <LegalSection id="processing" title="7. Refund Processing Time">
        <p>
          When a refund has been approved, we will process it within 3 business days. The refund is
          issued back to the original payment method used at checkout. Depending on your bank or
          card issuer, funds typically take <strong>5 to 10 business days</strong> to appear on your
          statement after we send them. We are not responsible for delays introduced by your bank or
          card network.
        </p>
      </LegalSection>

      <LegalSection id="how" title="8. How to Request a Refund">
        <p>To request a refund, please contact us in either of the following ways:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Email{" "}
            <a href="mailto:support@schengenfile.com" className="text-[#2563eb] hover:underline">
              support@schengenfile.com
            </a>{" "}
            from the address associated with your account; or
          </li>
          <li>Open a message thread on the specific order inside your SchengenFile customer dashboard.</li>
        </ul>
        <p>
          Please include your order number and a short description of the reason for the request.
          Our support team will review your case and respond within 24 hours (Saudi Arabia standard
          time), and we will confirm in writing whether a refund is approved before it is issued.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="9. Contact">
        <p>
          SchengenFile is operated by Trip360 Travel & Tourism Co., Riyadh, Kingdom of Saudi Arabia.
          For any question about this Refund Policy, please write to{" "}
          <a href="mailto:support@schengenfile.com" className="text-[#2563eb] hover:underline">
            support@schengenfile.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
